// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { getAdminUsers, adminUpdateUser, adminDeleteUser, adminExportUserData, getBillingSummary, exportBillingSummary, getMistralPricing, updateMistralPricing, refreshMistralRate } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import ConfirmModal from '../components/ConfirmModal';
import FicheClient from '../components/FicheClient';
import ResumePoolLicence from '../components/ResumePoolLicence';
import SkeletonRows from '../components/SkeletonRows';
import { PALIERS_VENDABLES, PALIERS_HISTORIQUE, libellePalier } from '../lib/paliers';

const ROLES = ['user', 'partner', 'admin', 'dev'];
const ROLE_COLORS = {
  admin:   { bg: '#fef3c7', text: '#92400e' },
  dev:     { bg: '#ede9fe', text: '#5b21b6' },
  partner: { bg: '#e0f2fe', text: '#0369a1' },
  user:    { bg: '#f3f4f6', text: '#374151' },
};

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatRelativeTime = (isoDate, lang) => {
  if (!isoDate) return lang === 'en' ? 'never' : 'jamais';
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return lang === 'en' ? '<1h ago' : 'il y a <1h';
  if (diffH < 24) return lang === 'en' ? `${diffH}h ago` : `il y a ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return lang === 'en' ? `${diffD}d ago` : `il y a ${diffD}j`;
};

const EditQuotaCell = ({ quotaMax, quotaType, onSave, onCancel }) => {
  const [qMax, setQMax] = useState(quotaMax);
  const [qType, setQType] = useState(quotaType || 'annual');
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <input type="number" value={qMax} onChange={e => setQMax(Number(e.target.value))} min={0}
        style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }} />
      <select value={qType} onChange={e => setQType(e.target.value)}
        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
        <option value="annual">Annuel</option>
        <option value="monthly">Mensuel</option>
      </select>
      <button onClick={() => onSave({ quota_max: qMax, quota_type: qType })} style={{ padding: '3px 8px', borderRadius: 5, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 11, cursor: 'pointer' }}>OK</button>
      <button onClick={onCancel} style={{ padding: '3px 6px', borderRadius: 5, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--gray)', fontSize: 11, cursor: 'pointer' }}>✕</button>
    </div>
  );
};

const EditTierCell = ({ tierCode, onSave, onCancel }) => {
  const [val, setVal] = useState(tierCode || '');
  const { t } = useI18n();
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select value={val} onChange={e => setVal(e.target.value)}
        style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
        <option value="">{t('billing_no_tier')}</option>
        {/* Assignation : seulement ce qui est vendable — PRO_PLUS est retiré
            de la grille, y rattacher un nouveau compte serait une erreur. */}
        {PALIERS_VENDABLES.map(code => (
          <option key={code} value={code}>{libellePalier(code)}</option>
        ))}
      </select>
      <button onClick={() => onSave(val)} style={{ padding: '3px 8px', borderRadius: 5, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 11, cursor: 'pointer' }}>OK</button>
      <button onClick={onCancel} style={{ padding: '3px 6px', borderRadius: 5, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--gray)', fontSize: 11, cursor: 'pointer' }}>✕</button>
    </div>
  );
};

const EditCell = ({ value, type, options, onSave, onCancel }) => {
  const [val, setVal] = useState(value);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {type === 'select' ? (
        <select value={val} onChange={e => setVal(e.target.value)}
          style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type="number" value={val} onChange={e => setVal(Number(e.target.value))} min={0}
          style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }} />
      )}
      <button onClick={() => onSave(val)} style={{ padding: '3px 8px', borderRadius: 5, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 11, cursor: 'pointer' }}>OK</button>
      <button onClick={onCancel} style={{ padding: '3px 6px', borderRadius: 5, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--gray)', fontSize: 11, cursor: 'pointer' }}>✕</button>
    </div>
  );
};

const AdminUsers = () => {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [editCell, setEditCell]       = useState(null); // { userId, field }
  const [confirmState, setConfirmState] = useState(null);
  const [exportingId, setExportingId]   = useState(null);
  const [search, setSearch]           = useState('');
  const [billingMonth, setBillingMonth]     = useState(getCurrentMonth());
  // 'mois' | 'glissant' (12 mois) | 'arr' (projection annuelle).
  const [billingVue, setBillingVue]         = useState('mois');
  const [billingData, setBillingData]       = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError]     = useState('');
  const [pricing, setPricing]               = useState(null);
  const [pricingEditing, setPricingEditing] = useState(false);
  const [pricingDraft, setPricingDraft]     = useState(null);
  const [pricingSaving, setPricingSaving]   = useState(false);
  const [pricingError, setPricingError]     = useState('');
  const [billingScopeMode, setBillingScopeMode]         = useState('total'); // 'total' | 'user' | 'forfait' | 'licence'
  const [billingScopeUserId, setBillingScopeUserId]     = useState('');
  const [billingScopeTier, setBillingScopeTier]         = useState('PRO');
  const [billingScopeLicenseId, setBillingScopeLicenseId] = useState('');
  const [summaryMode, setSummaryMode]       = useState('user'); // 'user' | 'forfait' | 'licence'
  const [summaryUserId, setSummaryUserId]   = useState('');
  const [summaryTier, setSummaryTier]       = useState('PRO');
  const [summaryLicenseId, setSummaryLicenseId] = useState('');
  const [ficheUserId, setFicheUserId]       = useState(null);   // fiche client ouverte
  const { user: me } = useAuth();
  const { t, lang } = useI18n();

  // L'e-mail ouvre la fiche client (chantier SaaS 5bis) — dans TOUS les tableaux,
  // Rentabilité comprise : c'est là que le pool d'un Cabinet se consulte désormais.
  const LienFiche = ({ userId, children }) => (
    <button type="button" onClick={() => setFicheUserId(userId)} title={t('fiche_ouvrir')}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit',
               font: 'inherit', textAlign: 'left', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
      {children}
    </button>
  );

  const formatDate = (d) => !d ? '-' : new Date(d).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // OCR : état EFFECTIF, en lecture seule. Ce fut longtemps une bascule sur
  // `ocr_enabled` — or tous les paliers de la grille donnent l'OCR, si bien que
  // remettre ce drapeau à 0 ne coupait plus rien et que la colonne affichait ❌
  // à des comptes qui océrisaient très bien. Le serveur rend désormais
  // l'ORIGINE du droit (`ocr_origine`), et l'écran se contente de la dire.
  // Facturé : le RÉEL (ce que Stripe a encaissé, remises comprises) dès qu'il
  // existe, le théorique de la grille sinon — et l'écran dit lequel des deux il
  // montre. Les confondre a fait afficher 49 € pour un abonnement réglé 5,88 €
  // après remise (16/09/2026) ; n'afficher que le réel ferait disparaître les
  // clients facturés par QONTO, qui n'ont pas de facture Stripe.
  const montantFacture = (ligne) => {
    if (ligne.facture_reelle_eur != null) {
      const detail = [t('billing_reel_infobulle')];
      if (ligne.facture_theorique_eur != null) detail.push(`${t('billing_col_billed')} ${t('billing_theorique_suffixe')} : ${ligne.facture_theorique_eur.toFixed(2)}€`);
      if (ligne.remise_eur) detail.push(`${t('billing_remise_label')} : ${ligne.remise_eur.toFixed(2)}€`);
      return { valeur: ligne.facture_reelle_eur, marge: ligne.marge_reelle_eur, estime: false, titre: detail.join(' · ') };
    }
    return { valeur: ligne.facture_theorique_eur, marge: ligne.marge_eur, estime: true,
             titre: t('billing_theorique_infobulle') };
  };

  const libelleOcr = (origine) => (
    origine === 'compte'  ? t('users_ocr_origine_compte')  :
    origine === 'licence' ? t('users_ocr_origine_licence') :
    origine === 'forfait' ? t('users_ocr_origine_forfait') :
                            t('users_ocr_origine_aucune')
  );

  const load = async () => {
    setLoading(true); setError('');
    try { const data = await getAdminUsers(); setUsers(data.users || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadBilling = async (month, vue) => {
    setBillingLoading(true); setBillingError('');
    try { setBillingData(await getBillingSummary(month, vue)); }
    catch (e) { setBillingError(e.message); }
    finally { setBillingLoading(false); }
  };

  useEffect(() => { loadBilling(billingMonth, billingVue); }, [billingMonth, billingVue]);

  const loadPricing = async () => {
    try { setPricing(await getMistralPricing()); }
    catch (e) { setPricingError(e.message); }
  };

  useEffect(() => { loadPricing(); }, []);

  const startEditPricing = () => {
    setPricingDraft({
      input_usd_per_million_tokens: pricing.input_usd_per_million_tokens,
      output_usd_per_million_tokens: pricing.output_usd_per_million_tokens,
      ocr_usd_per_page: pricing.ocr_usd_per_page,
    });
    setPricingError('');
    setPricingEditing(true);
  };

  const savePricing = async () => {
    setPricingSaving(true); setPricingError('');
    try {
      const updated = await updateMistralPricing(pricingDraft);
      setPricing(updated);
      setPricingEditing(false);
      loadBilling(billingMonth);
    } catch (e) { setPricingError(e.message); }
    finally { setPricingSaving(false); }
  };

  const refreshRate = async () => {
    setPricingSaving(true); setPricingError('');
    try { setPricing(await refreshMistralRate()); }
    catch (e) { setPricingError(e.message); }
    finally { setPricingSaving(false); }
  };

  const handleSaveField = async (userId, field, value) => {
    try {
      await adminUpdateUser(userId, { [field]: value });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, [field]: value } : u));
      setEditCell(null);
    } catch (e) { alert(t('err_prefix') + e.message); }
  };

  const handleSaveQuota = async (userId, payload) => {
    try {
      await adminUpdateUser(userId, payload);
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, ...payload } : u));
      setEditCell(null);
    } catch (e) { alert(t('err_prefix') + e.message); }
  };

  const handleSaveTier = async (userId, code) => {
    try {
      await adminUpdateUser(userId, { pricing_tier_code: code || null });
      await load();
      setEditCell(null);
    } catch (e) { alert(t('err_prefix') + e.message); }
  };

  const formatResetDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return null; }
  };

  // Export RGPD d'un compte (Art. 20). Sert à honorer une demande arrivée à
  // privacy@evalens.fr : jusqu'au 5/08/2026 rien ne le permettait sans requête
  // SQL sur le serveur, la route self-service exigeant le jeton du client.
  // Proposé aussi sur son propre compte — un export ne détruit rien — et sur
  // les sièges de licence, contrairement à la suppression.
  const handleExport = async (userId, email) => {
    setExportingId(userId);
    try {
      await adminExportUserData(userId, email);
    } catch (e) { alert(t('err_prefix') + e.message); }
    finally { setExportingId(null); }
  };

  const handleDelete = (userId, email) => {
    setConfirmState({
      message: `${t('user_delete_confirm')}\n\n${email}`,
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await adminDeleteUser(userId);
          setUsers(prev => prev.filter(u => u.user_id !== userId));
        } catch (e) { alert(t('err_prefix') + e.message); }
      }
    });
  };

  const licenseRoleLabel = (role, estCabinet = false) => {
    if (role === 'member') return t(estCabinet ? 'license_role_member_cabinet' : 'license_role_member');
    if (role === 'license_admin') return t(estCabinet ? 'license_role_admin_cabinet' : 'license_role_admin');
    return role || '-';
  };

  // ⭐ Le rôle d'un compte, DIT PAREIL DANS TOUS LES TABLEAUX (chantier ⑪).
  // Dans une licence, c'est `license_role` qui compte : `role` est le rôle
  // PLATEFORME, `user` pour tout client — Rentabilité affichait donc
  // l'administrateur d'un Cabinet comme un collègue ordinaire.
  // `license_id` est exigé : un titulaire détaché garde `license_role`.
  const libelleRole = (u) => (u.license_id && u.license_role
    ? licenseRoleLabel(u.license_role, u.est_cabinet)
    : (u.role || '—'));

  // Palier d'un compte, et pour un Cabinet le nom du cabinet : sans lui, tous
  // les Cabinets se confondent sous le même badge.
  const badgePalier = (u) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#e0f2fe', color: '#0369a1' }}>{u.tier_nom}</span>
      {u.est_cabinet && u.license_nom && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.license_nom}</span>}
    </span>
  );

  // Un compte révoqué ne pouvait pas se distinguer d'un compte actif : la route
  // `/api/admin/users` ne remontait tout simplement pas `is_active` (constaté par
  // Marc le 29/07/2026, sur des sièges détachés d'une licence supprimée).
  const BadgeRevoque = ({ actif }) => actif === false ? (
    <span style={{
      marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 6px',
      borderRadius: 99, background: 'rgba(239,68,68,0.1)', color: '#ef4444',
      whiteSpace: 'nowrap',
    }}>
      {t('ent_seats_revoked')}
    </span>
  ) : null;

  // ⚠️ « Aucun forfait » ne distingue pas un compte d'essai d'un ex-abonné.
  // Le webhook de résiliation efface le palier : un client résilié — donc
  // BLOQUÉ — s'affichait exactement comme un nouvel inscrit qui n'a encore rien
  // consommé (signalé par Marc le 16/09/2026). Le statut d'abonnement lève
  // l'ambiguïté, et seulement quand il y a quelque chose à dire.
  const BadgeAbonnement = ({ statut }) => {
    const etats = {
      canceled:            { libelle: t('users_abo_resilie'),  fond: 'rgba(239,68,68,0.1)',  texte: '#ef4444' },
      unpaid:              { libelle: t('users_abo_impaye'),   fond: 'rgba(239,68,68,0.1)',  texte: '#ef4444' },
      incomplete_expired:  { libelle: t('users_abo_expire'),   fond: 'rgba(239,68,68,0.1)',  texte: '#ef4444' },
      past_due:            { libelle: t('users_abo_retard'),   fond: 'rgba(245,158,11,0.12)', texte: '#b45309' },
    };
    const etat = etats[statut];
    if (!etat) return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('billing_no_tier')}</span>;
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
        background: etat.fond, color: etat.texte, whiteSpace: 'nowrap',
      }}>
        {etat.libelle}
      </span>
    );
  };

  const searchFilter = (list) => search.trim()
    ? list.filter(u => u.email.toLowerCase().includes(search.toLowerCase()))
    : list;

  const forfaitUsers = searchFilter(users.filter(u => u.category === 'forfait'));
  const licenceUsers = searchFilter(users.filter(u => u.category === 'licence'));
  const autresUsers  = searchFilter(users.filter(u => u.category === 'autres'));

  // Licences distinctes présentes parmi les users chargés (pour le sélecteur du résumé)
  const distinctLicences = Array.from(
    new Map(licenceUsers.map(u => [u.license_id, { id: u.license_id, nom: u.license_nom }])).values()
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {confirmState && <ConfirmModal message={confirmState.message} onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)} />}
      {ficheUserId && <FicheClient userId={ficheUserId} onClose={() => setFicheUserId(null)} onOuvrirFiche={setFicheUserId} />}

      <main className="container mx-auto px-4 py-8 max-w-6xl flex-1">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--black)', marginBottom: 4 }}>
              👥 {t('admin_users_title')}
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {users.length} {users.length > 1 ? t('user_count_pl') : t('user_count')}
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm">{t('btn_refresh')}</Button>
        </div>

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray)', background: 'var(--bg-input)', marginBottom: 20, maxWidth: 360 }}>
          <span style={{ color: 'var(--text-muted)' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('search_by_email')}
            style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, color: 'var(--black)', outline: 'none' }} />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>✕</button>}
        </div>

        {loading ? (
          <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody><SkeletonRows cols={8} rows={6} /></tbody>
            </table>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#ef4444' }}>❌ {error}</div>
        ) : (
          <>
            {/* ============ PRO / PRO+ ============ */}
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)', margin: '20px 0 10px' }}>{t('users_section_forfait')}</h2>
            {forfaitUsers.length === 0 ? (
              <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>{t('users_empty_category')}</div>
            ) : (
              <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                      {['Email', t('user_role'), t('billing_col_tier'), t('users_col_consommation'), 'OCR', t('user_created'), 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {forfaitUsers.map((u, i) => {
                      const isMe = u.user_id === me?.id;
                      const rc   = ROLE_COLORS[u.role] || ROLE_COLORS.user;
                      const editingRole = editCell?.userId === u.user_id && editCell?.field === 'role';
                      const editingTier = editCell?.userId === u.user_id && editCell?.field === 'tier';
                      return (
                        <tr key={u.user_id} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ fontWeight: isMe ? 600 : 400, color: 'var(--black)' }}><LienFiche userId={u.user_id}>{u.email}</LienFiche><BadgeRevoque actif={u.is_active} /></div>
                            {isMe && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t('its_you')}</span>}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            {u.est_cabinet ? (
                              // Rôle dans le Cabinet, non modifiable ici : le serveur
                              // refuse de toucher au rôle d'un compte rattaché à une
                              // licence (cf `admin_update_user`).
                              <span style={{ color: 'var(--black)' }}>{libelleRole(u)}</span>
                            ) : editingRole ? (
                              <EditCell value={u.role} type="select" options={ROLES}
                                onSave={v => handleSaveField(u.user_id, 'role', v)}
                                onCancel={() => setEditCell(null)} />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: rc.bg, color: rc.text }}>{u.role}</span>
                                {!isMe && <button onClick={() => setEditCell({ userId: u.user_id, field: 'role' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>✏️</button>}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            {u.est_cabinet ? (
                              // Le palier d'un Cabinet suit son abonnement Stripe :
                              // pas de ✏️ (refusé par le serveur de toute façon).
                              badgePalier(u)
                            ) : editingTier ? (
                              <EditTierCell tierCode={u.tier_code}
                                onSave={code => handleSaveTier(u.user_id, code)}
                                onCancel={() => setEditCell(null)} />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {u.tier_nom
                                  ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#e0f2fe', color: '#0369a1' }}>{u.tier_nom}</span>
                                  : <BadgeAbonnement statut={u.subscription_status} />}
                                <button onClick={() => setEditCell({ userId: u.user_id, field: 'tier' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>✏️</button>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--black)', whiteSpace: 'nowrap' }}>
                            {u.nb_analyses_month} {t('users_consommation_month_suffix')} · {u.nb_analyses_total} {t('users_consommation_total_suffix')}
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            <span title={libelleOcr(u.ocr_origine)} style={{ fontSize: 16 }}>
                              {u.ocr_origine ? '✅' : '❌'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(u.created_at)}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <button onClick={() => handleExport(u.user_id, u.email)}
                                disabled={exportingId === u.user_id}
                                title={t('user_export_rgpd_title')}
                                style={{ padding: '4px 8px', borderRadius: 6, background: 'var(--bg-input)', border: '1px solid var(--gray)', color: 'var(--text-muted)', fontSize: 11, cursor: exportingId === u.user_id ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                                {exportingId === u.user_id ? t('user_export_rgpd_loading') : t('user_export_rgpd')}
                              </button>
                              {/* Un siège de Cabinet ne se supprime pas d'ici : le
                                  serveur refuse tout compte rattaché à une licence
                                  (`admin_delete_user`). Il se révoque depuis Clients
                                  Entreprise ; le titulaire, lui, part en résiliant. */}
                              {u.est_cabinet ? (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{t('users_cabinet_gere_licences')}</span>
                              ) : !isMe && (
                                <button onClick={() => handleDelete(u.user_id, u.email)}
                                  style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>
                                  {t('delete')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ============ LICENCE ============ */}
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)', margin: '28px 0 10px' }}>{t('users_section_licence')}</h2>
            {licenceUsers.length === 0 ? (
              <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>{t('users_empty_category')}</div>
            ) : (
              <>
                <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                        {['Email', t('users_col_licence'), t('user_role'), t('users_col_pool'), t('users_col_pool_consumption'), t('user_created')].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {licenceUsers.map((u, i) => {
                        const isMe = u.user_id === me?.id;
                        const poolMax = u.pool_analyses_count;
                        const poolUsed = u.pool_used || 0;
                        const poolPct = poolMax > 0 ? Math.min(100, Math.round((poolUsed / poolMax) * 100)) : 0;
                        return (
                          <tr key={u.user_id} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                            <td style={{ padding: '10px 14px' }}>
                              <div style={{ fontWeight: isMe ? 600 : 400, color: 'var(--black)' }}><LienFiche userId={u.user_id}>{u.email}</LienFiche><BadgeRevoque actif={u.is_active} /></div>
                              {isMe && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t('its_you')}</span>}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#ede9fe', color: '#5b21b6' }}>{u.license_nom}</span>
                            </td>
                            <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--black)' }}>{licenseRoleLabel(u.license_role)}</td>
                            <td style={{ padding: '10px 14px', color: 'var(--black)', whiteSpace: 'nowrap' }}>
                              {poolMax ?? '∞'} {u.pool_analyses_type === 'annual' ? t('pool_type_annual') : t('pool_type_monthly')}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              {poolMax ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ width: 60, height: 6, background: 'var(--gray)', borderRadius: 99, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${poolPct}%`, background: poolPct >= 90 ? '#ef4444' : poolPct >= 70 ? '#f59e0b' : '#22c55e', borderRadius: 99 }} />
                                  </div>
                                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{poolUsed}/{poolMax}</span>
                                </div>
                              ) : (
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{poolUsed}</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(u.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{t('users_licence_managed_note')}</p>
              </>
            )}

            {/* ============ AUTRES ============ */}
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--black)', margin: '28px 0 10px' }}>{t('users_section_autres')}</h2>
            {autresUsers.length === 0 ? (
              <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>{t('users_empty_category')}</div>
            ) : (
              <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                      {['Email', t('user_role'), t('billing_col_tier'), 'Quota / Période', t('user_quota_used'), 'OCR', t('user_created'), 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {autresUsers.map(function(u, i) {
                      const isMe = u.user_id === me?.id;
                      const rc   = ROLE_COLORS[u.role] || ROLE_COLORS.user;
                      const pct  = u.quota_max > 0 ? Math.min(100, Math.round((u.quota_used / u.quota_max) * 100)) : 0;
                      const isUnlimited = ['admin', 'dev'].includes(u.role);
                      const editingRole  = editCell?.userId === u.user_id && editCell?.field === 'role';
                      const editingQuota = editCell?.userId === u.user_id && editCell?.field === 'quota';
                      const editingTier  = editCell?.userId === u.user_id && editCell?.field === 'tier';
                      const isMonthly = u.quota_type === 'monthly';
                      const resetStr = formatResetDate(u.quota_reset_at);
                      return (
                        <tr key={u.user_id} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ fontWeight: isMe ? 600 : 400, color: 'var(--black)' }}><LienFiche userId={u.user_id}>{u.email}</LienFiche><BadgeRevoque actif={u.is_active} /></div>
                            {isMe && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t('its_you')}</span>}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            {u.est_cabinet ? (
                              // Rôle dans le Cabinet, non modifiable ici : le serveur
                              // refuse de toucher au rôle d'un compte rattaché à une
                              // licence (cf `admin_update_user`).
                              <span style={{ color: 'var(--black)' }}>{libelleRole(u)}</span>
                            ) : editingRole ? (
                              <EditCell value={u.role} type="select" options={ROLES}
                                onSave={v => handleSaveField(u.user_id, 'role', v)}
                                onCancel={() => setEditCell(null)} />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: rc.bg, color: rc.text }}>{u.role}</span>
                                {!isMe && <button onClick={() => setEditCell({ userId: u.user_id, field: 'role' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>✏️</button>}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            {u.est_cabinet ? (
                              // Le palier d'un Cabinet suit son abonnement Stripe :
                              // pas de ✏️ (refusé par le serveur de toute façon).
                              badgePalier(u)
                            ) : editingTier ? (
                              <EditTierCell tierCode={u.tier_code}
                                onSave={code => handleSaveTier(u.user_id, code)}
                                onCancel={() => setEditCell(null)} />
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {u.tier_nom
                                  ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#e0f2fe', color: '#0369a1' }}>{u.tier_nom}</span>
                                  : <BadgeAbonnement statut={u.subscription_status} />}
                                <button onClick={() => setEditCell({ userId: u.user_id, field: 'tier' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>✏️</button>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                            {editingQuota ? (
                              <EditQuotaCell quotaMax={u.quota_max} quotaType={u.quota_type}
                                onSave={payload => handleSaveQuota(u.user_id, payload)}
                                onCancel={() => setEditCell(null)} />
                            ) : (
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span style={{ color: 'var(--black)' }}>{isUnlimited ? '∞' : u.quota_max}</span>
                                  {!isUnlimited && <button onClick={() => setEditCell({ userId: u.user_id, field: 'quota' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>✏️</button>}
                                </div>
                                {!isUnlimited && (
                                  <span style={{
                                    display: 'inline-block', fontSize: 10, fontWeight: 600,
                                    padding: '1px 7px', borderRadius: 99, marginTop: 3,
                                    background: isMonthly ? '#fed7aa' : '#e5e7eb',
                                    color: isMonthly ? '#9a3412' : '#374151'
                                  }}>
                                    {isMonthly ? '🔄 Mensuel' : '📅 Annuel'}
                                  </span>
                                )}
                                {!isUnlimited && isMonthly && resetStr && (
                                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                                    Reset le {resetStr}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            {isUnlimited ? (
                              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{u.nb_analyses_total} {u.nb_analyses_total > 1 ? t('analyses_label_pl') : t('analyses_label')}</span>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 60, height: 6, background: 'var(--gray)', borderRadius: 99, overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pct}%`, background: pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e', borderRadius: 99 }} />
                                </div>
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.quota_used}/{u.quota_max}</span>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            <span title={libelleOcr(u.ocr_origine)} style={{ fontSize: 16 }}>
                              {u.ocr_origine ? '✅' : '❌'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(u.created_at)}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <button onClick={() => handleExport(u.user_id, u.email)}
                                disabled={exportingId === u.user_id}
                                title={t('user_export_rgpd_title')}
                                style={{ padding: '4px 8px', borderRadius: 6, background: 'var(--bg-input)', border: '1px solid var(--gray)', color: 'var(--text-muted)', fontSize: 11, cursor: exportingId === u.user_id ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                                {exportingId === u.user_id ? t('user_export_rgpd_loading') : t('user_export_rgpd')}
                              </button>
                              {/* Un siège de Cabinet ne se supprime pas d'ici : le
                                  serveur refuse tout compte rattaché à une licence
                                  (`admin_delete_user`). Il se révoque depuis Clients
                                  Entreprise ; le titulaire, lui, part en résiliant. */}
                              {u.est_cabinet ? (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{t('users_cabinet_gere_licences')}</span>
                              ) : !isMe && (
                                <button onClick={() => handleDelete(u.user_id, u.email)}
                                  style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 11, cursor: 'pointer' }}>
                                  {t('delete')}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* 📊 Résumé d'utilisation — conditionné à une sélection (user / forfait / licence) */}
        {!loading && !error && (
          <div style={{ marginTop: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--black)' }}>{t('summary_title')}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <select value={summaryMode} onChange={e => setSummaryMode(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                  <option value="user">{t('summary_mode_user')}</option>
                  <option value="forfait">{t('summary_mode_forfait')}</option>
                  <option value="licence">{t('summary_mode_licence')}</option>
                </select>
                {summaryMode === 'user' && (
                  <select value={summaryUserId} onChange={e => setSummaryUserId(e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                    <option value="">{t('stat_choose_placeholder')}</option>
                    {users.map(u => <option key={u.user_id} value={u.user_id}>{u.email}</option>)}
                  </select>
                )}
                {summaryMode === 'forfait' && (
                  <select value={summaryTier} onChange={e => setSummaryTier(e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                    {/* Filtre de facturation : PRO_PLUS reste, sinon
                        l'historique de ces comptes deviendrait introuvable. */}
                    {PALIERS_HISTORIQUE.map(code => (
                      <option key={code} value={code}>{libellePalier(code)}</option>
                    ))}
                  </select>
                )}
                {summaryMode === 'licence' && (
                  <select value={summaryLicenseId} onChange={e => setSummaryLicenseId(e.target.value)}
                    style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                    <option value="">{t('stat_choose_placeholder')}</option>
                    {distinctLicences.map(l => <option key={l.id} value={l.id}>{l.nom}</option>)}
                  </select>
                )}
              </div>
            </div>

            {summaryMode === 'user' && (() => {
              const u = users.find(x => x.user_id === summaryUserId);
              if (!u) {
                return <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>{t('summary_pick_user')}</div>;
              }
              return (
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: 16, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>
                  <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>Email</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{u.email}</p></div>
                  <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('user_role')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{libelleRole(u)}</p></div>
                  {u.category === 'forfait' && (<>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('billing_col_tier')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{u.est_cabinet && u.license_nom ? `${u.tier_nom} · ${u.license_nom}` : u.tier_nom}</p></div>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('users_col_consommation')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{u.nb_analyses_month} {t('users_consommation_month_suffix')} · {u.nb_analyses_total} {t('users_consommation_total_suffix')}</p></div>
                  </>)}
                  {u.category === 'licence' && (<>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('users_col_licence')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{u.license_nom}</p></div>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('users_col_pool_consumption')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{u.pool_used || 0}/{u.pool_analyses_count ?? '∞'}</p></div>
                  </>)}
                  {u.category === 'autres' && (<>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>Quota</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{['admin', 'dev'].includes(u.role) ? '∞' : u.quota_max}</p></div>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('user_quota_used')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{['admin', 'dev'].includes(u.role) ? u.nb_analyses_total : u.quota_used}</p></div>
                  </>)}
                </div>
              );
            })()}

            {summaryMode === 'forfait' && (() => {
              const rows = users.filter(u => u.category === 'forfait' && u.tier_code === summaryTier);
              if (rows.length === 0) {
                return <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>{t('users_empty_category')}</div>;
              }
              const totalMonth = rows.reduce((s, r) => s + r.nb_analyses_month, 0);
              const totalAll = rows.reduce((s, r) => s + r.nb_analyses_total, 0);
              return (
                <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                        {['Email', t('user_role'), t('users_consommation_month_suffix'), t('users_consommation_total_suffix')].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.user_id} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                          <td style={{ padding: '10px 14px', color: 'var(--black)' }}>{r.email}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{libelleRole(r)}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--black)' }}>{r.nb_analyses_month}</td>
                          <td style={{ padding: '10px 14px', color: 'var(--black)' }}>{r.nb_analyses_total}</td>
                        </tr>
                      ))}
                      <tr style={{ background: 'var(--white)', borderTop: '2px solid var(--gray)' }}>
                        <td colSpan={2} style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--black)' }}>{t('billing_total_label')}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--black)' }}>{totalMonth}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--black)' }}>{totalAll}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {summaryMode === 'licence' && (() => {
              if (!summaryLicenseId) {
                return <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>{t('summary_pick_user')}</div>;
              }
              const members = users.filter(u => u.category === 'licence' && u.license_id === summaryLicenseId);
              const lic = members[0];
              if (!lic) return null;
              const poolMax = lic.pool_analyses_count;
              const poolUsed = lic.pool_used || 0;
              const poolPct = poolMax > 0 ? Math.min(100, Math.round((poolUsed / poolMax) * 100)) : 0;
              return (
                <>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: 16, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', marginBottom: 16 }}>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('users_col_licence')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{lic.license_nom}</p></div>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('users_col_pool')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{poolMax ?? '∞'} {lic.pool_analyses_type === 'annual' ? t('pool_type_annual') : t('pool_type_monthly')}</p></div>
                    <div><p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>{t('users_col_pool_consumption')}</p><p style={{ fontSize: 14, fontWeight: 600, color: 'var(--black)', margin: 0 }}>{poolUsed}/{poolMax ?? '∞'} {poolMax ? `(${poolPct}%)` : ''}</p></div>
                  </div>
                  <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                          {['Email', t('user_role'), t('user_created')].map(h => (
                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((r, i) => (
                          <tr key={r.user_id} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                            <td style={{ padding: '10px 14px', color: 'var(--black)' }}>{r.email}</td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{licenseRoleLabel(r.license_role)}</td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 12 }}>{formatDate(r.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* 💰 Rentabilité (coût réel Mistral vs facturé théorique, par user/mois) */}
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--black)' }}>
              {t('billing_section_title')}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('billing_period_label')}</label>
              <input type="month" value={billingMonth} onChange={e => setBillingMonth(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }} />
              <select value={billingVue} onChange={e => setBillingVue(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                <option value="mois">{t('billing_vue_mois')}</option>
                <option value="glissant">{t('billing_vue_glissant')}</option>
                <option value="arr">{t('billing_vue_arr')}</option>
              </select>
              <Button onClick={() => exportBillingSummary(billingMonth).catch(e => alert(e.message))} variant="outline" size="sm">{t('export_excel')}</Button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <select value={billingScopeMode} onChange={e => setBillingScopeMode(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
              <option value="total">{t('billing_scope_total')}</option>
              <option value="user">{t('summary_mode_user')}</option>
              <option value="forfait">{t('summary_mode_forfait')}</option>
              <option value="licence">{t('summary_mode_licence')}</option>
            </select>
            {billingScopeMode === 'user' && (
              <select value={billingScopeUserId} onChange={e => setBillingScopeUserId(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                <option value="">{t('stat_choose_placeholder')}</option>
                {users.map(u => <option key={u.user_id} value={u.user_id}>{u.email}</option>)}
              </select>
            )}
            {billingScopeMode === 'forfait' && (
              <select value={billingScopeTier} onChange={e => setBillingScopeTier(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                {PALIERS_HISTORIQUE.map(code => (
                  <option key={code} value={code}>{libellePalier(code)}</option>
                ))}
              </select>
            )}
            {billingScopeMode === 'licence' && (
              <select value={billingScopeLicenseId} onChange={e => setBillingScopeLicenseId(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }}>
                <option value="">{t('stat_choose_placeholder')}</option>
                {distinctLicences.map(l => <option key={l.id} value={l.id}>{l.nom}</option>)}
              </select>
            )}
          </div>

          {/* Une projection n'est pas une mesure : le dire, sinon le chiffre
              sera lu comme un résultat constaté. */}
          {billingData?.projection && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--warning, #f59e0b)', fontSize: 12, color: 'var(--text-muted)' }}>
              {t('billing_projection_avertissement')}
            </div>
          )}

          {pricing && (
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', fontSize: 12 }}>
              {!pricingEditing ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: 'var(--black)' }}>{t('mistral_pricing_title')}</span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {t('mistral_pricing_input')}: <b style={{ color: 'var(--black)' }}>${pricing.input_usd_per_million_tokens}/M</b> → <b style={{ color: 'var(--black)' }}>{pricing.input_eur_per_million_tokens}€/M</b>
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {t('mistral_pricing_output')}: <b style={{ color: 'var(--black)' }}>${pricing.output_usd_per_million_tokens}/M</b> → <b style={{ color: 'var(--black)' }}>{pricing.output_eur_per_million_tokens}€/M</b>
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {t('mistral_pricing_ocr')}: <b style={{ color: 'var(--black)' }}>${pricing.ocr_usd_per_page}/page</b> → <b style={{ color: 'var(--black)' }}>{pricing.ocr_eur_per_page}€/page</b>
                    </span>
                    <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {t('mistral_pricing_rate')}: <b style={{ color: 'var(--black)' }}>{pricing.eur_usd_rate}</b>
                      {' '}({formatRelativeTime(pricing.eur_usd_rate_updated_at, lang)})
                      <button onClick={refreshRate} disabled={pricingSaving} title={t('mistral_pricing_refresh_rate')}
                        style={{ padding: '1px 6px', borderRadius: 5, background: 'transparent', color: 'var(--blue)', border: '1px solid var(--blue)', fontSize: 11, cursor: 'pointer' }}>
                        🔄
                      </button>
                    </span>
                  </div>
                  <button onClick={startEditPricing} style={{ padding: '3px 10px', borderRadius: 5, background: 'transparent', color: 'var(--blue)', border: '1px solid var(--blue)', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
                    {t('mistral_pricing_edit')}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: 'var(--black)' }}>{t('mistral_pricing_title')}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)' }}>
                    {t('mistral_pricing_input')}
                    <input type="number" step="0.001" min="0" value={pricingDraft.input_usd_per_million_tokens}
                      onChange={e => setPricingDraft(d => ({ ...d, input_usd_per_million_tokens: Number(e.target.value) }))}
                      style={{ width: 72, padding: '3px 6px', borderRadius: 5, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }} />
                    $/M
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)' }}>
                    {t('mistral_pricing_output')}
                    <input type="number" step="0.001" min="0" value={pricingDraft.output_usd_per_million_tokens}
                      onChange={e => setPricingDraft(d => ({ ...d, output_usd_per_million_tokens: Number(e.target.value) }))}
                      style={{ width: 72, padding: '3px 6px', borderRadius: 5, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }} />
                    $/M
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)' }}>
                    {t('mistral_pricing_ocr')}
                    <input type="number" step="0.00001" min="0" value={pricingDraft.ocr_usd_per_page}
                      onChange={e => setPricingDraft(d => ({ ...d, ocr_usd_per_page: Number(e.target.value) }))}
                      style={{ width: 72, padding: '3px 6px', borderRadius: 5, border: '1px solid var(--gray)', background: 'var(--bg-input)', color: 'var(--black)', fontSize: 12 }} />
                    $/page
                  </label>
                  <button onClick={savePricing} disabled={pricingSaving} style={{ padding: '3px 10px', borderRadius: 5, background: 'var(--blue)', color: 'white', border: 'none', fontSize: 11, cursor: 'pointer' }}>
                    {pricingSaving ? '…' : t('mistral_pricing_save')}
                  </button>
                  <button onClick={() => setPricingEditing(false)} style={{ padding: '3px 8px', borderRadius: 5, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--gray)', fontSize: 11, cursor: 'pointer' }}>✕</button>
                  {pricingError && <span style={{ color: '#ef4444' }}>{pricingError}</span>}
                </div>
              )}
            </div>
          )}

          {billingLoading ? (
            <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody><SkeletonRows cols={8} rows={4} /></tbody>
              </table>
            </div>
          ) : billingError ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#ef4444' }}>❌ {billingError}</div>
          ) : !billingData || billingData.users.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>
              {t('billing_empty')}
            </div>
          ) : (() => {
            const ocrDetail = (u, costOcr) => {
              const pagesPart = (u.pages_ocr_mistral || u.pages_ocr_tesseract)
                ? ` (Mistral ${u.pages_ocr_mistral}p · Tesseract ${u.pages_ocr_tesseract}p)`
                : '';
              return `OCR ${costOcr}€${pagesPart}`;
            };
            // `resumes` : les résumés de pool partagé qui APPARTIENNENT à la section,
            // rendus sous son tableau. Ils formaient un bloc sans titre entre
            // « Forfaits » et « Licences entreprise », et le pool d'un Cabinet
            // (un forfait) passait pour une seconde apparition de la licence.
            const renderBillingTable = (rows, titleKey, resumes = [], explicationKey = 'billing_pool_explication') => {
              if (rows.length === 0) {
                return (
                  <div key={titleKey} style={{ marginBottom: 20 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--black)', marginBottom: 8 }}>{t(titleKey)}</h3>
                    <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)' }}>{t('users_empty_category')}</div>
                  </div>
                );
              }
              const totalNbAnalyses = rows.reduce((s, u) => s + u.nb_analyses, 0);
              const totalCout = rows.reduce((s, u) => s + u.cout_total_eur, 0);
              // ⚠️ Le coût moyen se divise par les analyses qui ont CONSOMMÉ, pas par
              // celles qui sont FACTURÉES : le numérateur inclut les analyses échouées
              // et l'ingestion des documents de chantier. Diviser par `nb_analyses`
              // (COMPLETED) majorait le coût moyen sans que rien ne le signale.
              const totalNbAvecCout = rows.reduce((s, u) => s + u.nb_analyses_avec_cout, 0);
              const totalCoutMoyen = totalNbAvecCout ? totalCout / totalNbAvecCout : null;
              // Le total additionne, ligne par ligne, le montant RÉELLEMENT
              // affiché — réel là où Stripe a facturé, théorique ailleurs. Un
              // total qui prendrait toujours le théorique contredirait sa propre
              // colonne dès la première remise.
              const hasFacture = rows.some(u => montantFacture(u).valeur != null);
              const totalFacture = hasFacture ? rows.reduce((s, u) => s + (montantFacture(u).valeur || 0), 0) : null;
              const totalMarge = hasFacture ? rows.reduce((s, u) => s + (montantFacture(u).marge || 0), 0) : null;
              const aEncaisse = rows.some(u => u.encaisse_ht_eur != null);
              const totalEncaisse = aEncaisse ? rows.reduce((s, u) => s + (u.encaisse_ht_eur || 0), 0) : null;
              return (
                <div key={titleKey} style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--black)', marginBottom: 8 }}>{t(titleKey)}</h3>
                  <div className="admin-table-wrapper" style={{ background: 'var(--bg-surface)', borderRadius: 10, border: '1px solid var(--gray)', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--white)', borderBottom: '1px solid var(--gray)' }}>
                          {['Email', t('user_role'), t('billing_col_tier'), t('billing_col_analyses'), t('billing_col_cost_avg'), t('billing_col_cost_real'), t('billing_col_billed'), t('billing_col_encaisse'), t('billing_col_margin')].map(h => (
                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--black)', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((u, i) => (
                          <tr key={u.user_id} style={{ borderBottom: '1px solid var(--gray)', background: i % 2 === 0 ? 'transparent' : 'rgba(128,128,128,0.03)' }}>
                            {/* Un compte supprimé garde sa ligne : le coût a bien
                                été payé à Mistral, le retirer fausserait le total
                                et la marge du mois. Il n'a simplement plus d'email. */}
                            <td style={{ padding: '10px 14px', color: u.compte_supprime ? 'var(--text-muted)' : 'var(--black)', fontStyle: u.compte_supprime ? 'italic' : 'normal' }}>
                              {u.compte_supprime ? t('billing_deleted_account') : <LienFiche userId={u.user_id}>{u.email}</LienFiche>}
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{u.compte_supprime ? '—' : libelleRole(u)}</td>
                            <td style={{ padding: '10px 14px' }}>
                              {u.tier_nom
                                ? badgePalier(u)
                                : u.license_nom
                                ? <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#ede9fe', color: '#5b21b6' }}>{u.license_nom}</span>
                                : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('billing_no_tier')}</span>}
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--black)' }}>{u.nb_analyses}</td>
                            <td style={{ padding: '10px 14px', color: 'var(--black)' }}
                                title={u.cout_moyen_analyse_eur !== null
                                  ? `${t('billing_cost_avg_basis').replace('{n}', u.nb_analyses_avec_cout)} — `
                                    + `LLM ${(u.cout_llm_eur / u.nb_analyses_avec_cout).toFixed(3)}€ · `
                                    + `${ocrDetail(u, (u.cout_ocr_eur / u.nb_analyses_avec_cout).toFixed(3))} · `
                                    + `RAG ${(u.cout_rag_eur / u.nb_analyses_avec_cout).toFixed(3)}€`
                                  : undefined}>
                              {u.cout_moyen_analyse_eur !== null ? `${u.cout_moyen_analyse_eur.toFixed(3)}€` : '—'}
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--black)' }}
                                title={`LLM ${u.cout_llm_eur.toFixed(2)}€ · ${ocrDetail(u, u.cout_ocr_eur.toFixed(2))} · RAG ${u.cout_rag_eur.toFixed(2)}€`}>
                              {u.cout_total_eur.toFixed(2)}€
                            </td>
                            {(() => {
                              const f = montantFacture(u);
                              return (
                                <>
                                  <td style={{ padding: '10px 14px', color: 'var(--black)', fontStyle: f.estime ? 'italic' : 'normal' }}
                                      title={f.titre}>
                                    {f.valeur != null ? `${f.valeur.toFixed(2)}€${f.estime ? ' *' : ''}` : '—'}
                                  </td>
                                  {/* Encaissé : les factures réellement payées,
                                      avoirs exclus. Diverge du « Facturé » net
                                      dès qu'un changement de palier crée un
                                      crédit — les deux sont justes. */}
                                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}
                                      title={t('billing_col_encaisse_infobulle')}>
                                    {u.encaisse_ht_eur != null ? `${u.encaisse_ht_eur.toFixed(2)}€` : '—'}
                                  </td>
                                  <td style={{ padding: '10px 14px', fontWeight: 600, color: f.marge == null ? 'var(--text-muted)' : (f.marge >= 0 ? '#22c55e' : '#ef4444') }}>
                                    {f.marge != null ? `${f.marge.toFixed(2)}€` : '—'}
                                  </td>
                                </>
                              );
                            })()}
                          </tr>
                        ))}
                        <tr style={{ background: 'var(--white)', borderTop: '2px solid var(--gray)' }}>
                          <td colSpan={3} style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--black)' }}>{t('billing_total_label')}</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--black)' }}>{totalNbAnalyses}</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--black)' }}>
                            {totalCoutMoyen !== null ? `${totalCoutMoyen.toFixed(3)}€` : '—'}
                          </td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--black)' }}>{totalCout.toFixed(2)}€</td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--black)' }}>
                            {totalFacture !== null ? `${totalFacture.toFixed(2)}€` : '—'}
                          </td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--text-muted)' }}>
                            {totalEncaisse !== null ? `${totalEncaisse.toFixed(2)}€` : '—'}
                          </td>
                          <td style={{ padding: '10px 14px', fontWeight: 700, color: totalMarge === null ? 'var(--black)' : (totalMarge >= 0 ? '#22c55e' : '#ef4444') }}>
                            {totalMarge !== null ? `${totalMarge.toFixed(2)}€` : '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {rows.some(u => montantFacture(u).estime && montantFacture(u).valeur != null) && (
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                      {t('billing_theorique_note')}
                    </p>
                  )}
                  {resumes.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      {/* Sans titre, le bloc passait pour une ligne de plus du tableau. */}
                      <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)', margin: '0 0 2px' }}>{t('billing_pool_title')}</h4>
                      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 8px' }}>{t(explicationKey)}</p>
                      {resumes.map(l => <ResumePoolLicence key={l.license_id} licence={l} />)}
                    </div>
                  )}
                </div>
              );
            };

            const scopedUsers = billingScopeMode === 'total' ? billingData.users
              : billingScopeMode === 'user' ? billingData.users.filter(u => u.user_id === billingScopeUserId)
              : billingScopeMode === 'forfait' ? billingData.users.filter(u => u.category === 'forfait' && u.tier_code === billingScopeTier)
              : billingData.users.filter(u => u.category === 'licence' && u.license_id === billingScopeLicenseId);

            // Bloc résumé par licence (pool partagé, ne peut pas se calculer ligne par
            // ligne comme un forfait — cf _compute_facture_theorique_licence côté backend).
            const scopedLicences = (billingData.licences || []).filter(l => (
              billingScopeMode === 'total'
              || (billingScopeMode === 'licence' && l.license_id === billingScopeLicenseId)
              // Un Cabinet est un forfait, mais son pool reste partagé : son
              // résumé de pool suit le filtre « forfait Cabinet ».
              || (billingScopeMode === 'forfait' && billingScopeTier === 'CABINET' && l.est_cabinet)
            ));

            // ⭐ Le pool d'un Cabinet a QUITTÉ ce tableau (demande de Marc, 22/09/2026) :
            // il s'ouvre dans la fiche client du titulaire (`FicheClient`, même
            // source `billing-summary`). Une note le dit, sans quoi on le croirait perdu.
            return (
              <>
                {renderBillingTable(scopedUsers.filter(u => u.category === 'forfait'), 'users_section_forfait')}
                {scopedLicences.some(l => l.est_cabinet) && (
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '-8px 0 20px' }}>ℹ️ {t('fiche_pool_deplace')}</p>
                )}
                {renderBillingTable(scopedUsers.filter(u => u.category === 'licence'), 'users_section_licence',
                                    scopedLicences.filter(l => !l.est_cabinet))}
                {renderBillingTable(scopedUsers.filter(u => u.category === 'autres'), 'users_section_autres')}
              </>
            );
          })()}
        </div>
      </main>
    </div>
  );
};

export default AdminUsers;